import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Center,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'
import { usePopUpMessage } from '@hb/react'
import {
  ActionButton,
  completeGynIntakeForm,
  exchangeGynIntakeCode,
  GynCodeFormContent,
  gynIntakeApp,
  Loading,
  PageContainer,
  SolidActionButton,
  updateGynAnswersByCode,
} from '@hb/react-web'
import { FirebaseAppProvider, useFirebaseApp } from '@hb/react-web/src/contexts/FirebaseAppProvider'
import { FieldMapValue, GynIntakeFormData, User, WithId } from '@hb/shared'
import { Auth, signInWithCustomToken, signOut } from 'firebase/auth'
import { Functions } from 'firebase/functions'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

const formatTime = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

const FIVE_MINUTES_MS = 5 * 60 * 1000

const TimerBanner = ({ expiresAt, onExpired }: { expiresAt: number; onExpired: () => void }) => {
  const [remaining, setRemaining] = useState(() => Math.max(0, expiresAt - Date.now()))
  const expiredCallbackRef = useRef(false)

  useEffect(() => {
    const interval = setInterval(() => {
      const r = Math.max(0, expiresAt - Date.now())
      setRemaining(r)
      if (r === 0 && !expiredCallbackRef.current) {
        expiredCallbackRef.current = true
        onExpired()
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [expiresAt, onExpired])

  const isExpired = remaining === 0
  const isWarning = remaining <= FIVE_MINUTES_MS

  return (
    <Flex
      px={4}
      py={2}
      bg={isExpired ? 'red.500' : isWarning ? 'orange.400' : 'blue.500'}
      color="white"
      align="center"
      justify="center"
      gap={2}
      w="100%"
      fontWeight={500}
      fontSize="sm">
      {isExpired ? (
        <Text>Access expired. Please contact your provider for a new link.</Text>
      ) : (
        <Text>
          Access window: <strong>{formatTime(remaining)}</strong> remaining
        </Text>
      )}
    </Flex>
  )
}

const WarningModal = ({
  isOpen,
  onSave,
  isSaving,
  onClose,
}: {
  isOpen: boolean
  onSave: () => void
  isSaving: boolean
  onClose: () => void
}) => (
  <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false}>
    <ModalOverlay />
    <ModalContent>
      <ModalHeader>Access Expiring Soon</ModalHeader>
      <ModalBody>
        <Text>
          Your access to this form expires in less than 5 minutes. Please save your progress now to
          avoid losing your data.
        </Text>
      </ModalBody>
      <ModalFooter>
        <Button colorScheme="orange" isLoading={isSaving} onClick={onSave}>
          Save Progress
        </Button>
      </ModalFooter>
      <ModalCloseButton />
    </ModalContent>
  </Modal>
)

const CodeEntryForm = ({ onSubmit }: { onSubmit: (code: string) => void }) => {
  const [code, setCode] = useState('')
  return (
    <Center w="100%" h="100%">
      <Flex
        flexFlow="column"
        gap={4}
        p={8}
        bg="white"
        borderRadius="md"
        boxShadow="md"
        maxW="400px"
        w="100%">
        <Text fontSize="xl" fontWeight={600}>
          Enter Your Access Code
        </Text>
        <Text color="gray.600" fontSize="sm">
          Enter the code provided by your healthcare provider to access the intake form.
        </Text>
        <Input
          placeholder="e.g. A1B2C3D4"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && code.length > 0 && onSubmit(code)}
          size="lg"
          letterSpacing="widest"
          fontFamily="mono"
        />
        <SolidActionButton isDisabled={code.length === 0} onClick={() => onSubmit(code)} size="lg">
          Access Form
        </SolidActionButton>
      </Flex>
    </Center>
  )
}

const buildUserFromFormData = (patientId: string, formData: GynIntakeFormData): WithId<User> => ({
  id: patientId,
  fname: formData.userData.fname ?? '',
  lname: formData.userData.lname ?? '',
  email: formData.userData.email ?? '',
  // emailLower: (formData.userData.email ?? '').toLowerCase(),
  isInvite: formData.isInvite,
  createdOn: 0,
  forceUpdateOn: 0,
  middleName: formData.userData.middleName,
  nickname: formData.userData.nickname,
  pronouns: formData.userData.pronouns,
  dob: formData.userData.dob,
  phone: formData.userData.phone,
  address: formData.userData.address,
  voicemailOk: formData.userData.voicemailOk,
  textOk: formData.userData.textOk,
  placeOfBirth: formData.userData.placeOfBirth,
  primaryLanguage: formData.userData.primaryLanguage,
  race: formData.userData.race,
  ethnicity: formData.userData.ethnicity,
  educationStatus: formData.userData.educationStatus,
  employment: formData.userData.employment,
  emergencyContact: formData.userData.emergencyContact,
  preferredPharmacy: formData.userData.preferredPharmacy,
  maidenName: formData.userData.maidenName,
  gyn: (formData.userData.gyn ?? formData.gynData) as User['gyn'],
  insuranceInfo: formData.userData.insuranceInfo,
  partnerInfo: formData.userData.partnerInfo,
})

const GynIntakeFormContent = ({
  code,
  formData,
  onUserDataUpdate,
  onSessionEnd,
  auth: gynIntakeAuth,
  functions,
}: {
  code: string
  formData: GynIntakeFormData
  auth: Auth
  functions: Functions
  onUserDataUpdate: (userData: Partial<User>) => void
  onSessionEnd: () => void
}) => {
  const { showError, showSuccess } = usePopUpMessage()
  const [warningDismissed, setWarningDismissed] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const warningShownRef = useRef(false)

  const user = useMemo(() => buildUserFromFormData(formData.patientId, formData), [formData])

  useEffect(() => {
    if (warningDismissed) return
    const checkWarning = () => {
      const remaining = formData.expiresAt - Date.now()
      if (remaining <= FIVE_MINUTES_MS && remaining > 0 && !warningShownRef.current) {
        warningShownRef.current = true
        setShowWarning(true)
      }
    }
    const interval = setInterval(checkWarning, 1000)
    checkWarning()
    return () => clearInterval(interval)
  }, [formData.expiresAt, warningDismissed])

  const handleExpired = useCallback(async () => {
    await signOut(gynIntakeAuth)
    onSessionEnd()
  }, [onSessionEnd, gynIntakeAuth])

  const onSubmitOverride = useCallback(
    async (data: FieldMapValue) => {
      try {
        const result = await updateGynAnswersByCode(functions)({ code, data })
        if (result.data?.userData) {
          onUserDataUpdate(result.data.userData)
        }
        return { success: 'Saved' }
      } catch (error: any) {
        const message = error.message ?? 'An error occurred'
        showError(message)
        return { error: message }
      }
    },
    [code, onUserDataUpdate, showError, functions],
  )

  const handleSaveProgress = useCallback(async () => {
    setIsSaving(true)
    try {
      setShowWarning(false)
      setWarningDismissed(true)
      showSuccess('Progress saved', 'Use the SAVE button on each section to save your answers.')
    } finally {
      setIsSaving(false)
    }
  }, [showSuccess])

  const handleComplete = useCallback(async () => {
    try {
      await completeGynIntakeForm(functions)({ code })
    } catch {
      // Best-effort — still sign out even if the server call fails
    } finally {
      await signOut(gynIntakeAuth)
      onSessionEnd()
    }
  }, [code, onSessionEnd, gynIntakeAuth, functions])

  return (
    <Flex flexFlow="column" align="center" w="100%" h="100%" gap={2}>
      <TimerBanner expiresAt={formData.expiresAt} onExpired={handleExpired} />
      <Center w="100%" flex={1} minH={0} gap={2} overflow="hidden" flexFlow="column">
        <GynCodeFormContent user={user} onSubmitOverride={onSubmitOverride} />
      </Center>
      <Flex px={4} py={3} w="100%" justify="flex-end" borderTop="1px solid" borderColor="gray.200">
        <SolidActionButton colorScheme="green" onClick={handleComplete}>
          Done — Submit Form
        </SolidActionButton>
      </Flex>
      <WarningModal
        isOpen={showWarning}
        onSave={handleSaveProgress}
        isSaving={isSaving}
        onClose={() => setShowWarning(false)}
      />
    </Flex>
  )
}

const GynIntakeFormViewContent = ({
  auth: gynIntakeAuth,
  functions,
}: {
  auth: Auth
  functions: Functions
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [formData, setFormData] = useState<GynIntakeFormData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const numRetries = useRef(0)
  const codeParam = searchParams.get('code')

  const handleSessionEnd = useCallback(() => {
    setFormData(null)
    setSearchParams({}, { replace: true })
  }, [setSearchParams])

  const validateCode = useCallback(
    async (code: string) => {
      setLoading(true)
      setError(null)
      try {
        const result = await exchangeGynIntakeCode(functions)({ code })
        if (!result.data) {
          throw new Error('Invalid or expired code')
        }
        const { customToken, formData: fd } = result.data
        await signInWithCustomToken(gynIntakeAuth, customToken)

        setFormData(fd)
        setSearchParams({ code }, { replace: true })
      } catch (err: any) {
        const message = err.message ?? 'Invalid or expired code'
        setError(message)
        setFormData(null)
      } finally {
        setLoading(false)
      }
    },
    [setSearchParams, gynIntakeAuth, functions],
  )

  useEffect(() => {
    if (codeParam && !formData && !loading && !error) {
      validateCode(codeParam)
      numRetries.current += 1
    }
  }, [codeParam, formData, loading, validateCode, error])

  const handleUserDataUpdate = useCallback((userData: Partial<User>) => {
    setFormData(prev => (prev ? { ...prev, userData: { ...prev.userData, ...userData } } : prev))
  }, [])

  const pageContentProps = { pt: 0, pb: 0, w: '100%', px: 0, maxW: '100%' }

  return (
    <PageContainer fullHeight fullWidth contentProps={pageContentProps}>
      {loading ? (
        <Center w="100%" h="100%">
          <Loading text="Loading intake form..." />
        </Center>
      ) : formData ? (
        <GynIntakeFormContent
          auth={gynIntakeAuth}
          functions={functions}
          code={codeParam ?? ''}
          formData={formData}
          onUserDataUpdate={handleUserDataUpdate}
          onSessionEnd={handleSessionEnd}
        />
      ) : error && codeParam ? (
        <Center w="100%" h="100%">
          <Alert
            status="error"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            maxW="500px"
            borderRadius="md"
            p={8}>
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={2} fontSize="lg">
              Access Code Invalid
            </AlertTitle>
            <AlertDescription>
              {error}. Please contact your provider for a new intake link.
            </AlertDescription>
            <ActionButton
              mt={4}
              variant="outline"
              colorScheme="gray"
              onClick={() => setSearchParams({}, { replace: true })}>
              Try a Different Code
            </ActionButton>
          </Alert>
        </Center>
      ) : (
        <CodeEntryForm onSubmit={validateCode} />
      )}
    </PageContainer>
  )
}

const GynIntakeFormViewWrapper = () => {
  const { auth: gynIntakeAuth, functions } = useFirebaseApp()

  if (!gynIntakeAuth || !functions)
    return (
      <Center w="100%" h="100%">
        <Loading text="Loading intake form..." />
      </Center>
    )

  return <GynIntakeFormViewContent auth={gynIntakeAuth} functions={functions} />
}

export const GynIntakeFormView = () => (
  <FirebaseAppProvider app={gynIntakeApp}>
    <GynIntakeFormViewWrapper />
  </FirebaseAppProvider>
)
